'use client'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { formatMoney, reviewCategoryLabel, statusColor, statusLabel } from '@/lib/ap/invoices'
import { formatCalendarDate } from '@/lib/dates'
import { workbenchLink } from '@/lib/ai/page-context'
import type { ChatAttachment, InvoiceAttachmentData } from '@/lib/ai/types'

function dbId(data: InvoiceAttachmentData): number | null {
  const raw = data.invoice_db_id ?? data.id
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null
}

export function StatusPill({ status }: { status?: string | null }) {
  if (!status) return null
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium', statusColor(status))}>
      {statusLabel(status)}
    </span>
  )
}

/** "invoice" attachment — compact summary of one invoice with a workbench deep-link. */
export function InvoiceCard({ attachment, onNavigate }: { attachment: ChatAttachment; onNavigate?: () => void }) {
  const data = (attachment.data ?? {}) as InvoiceAttachmentData
  const id = dbId(data)
  const pos = data.related_po_numbers ?? []
  const grns = data.related_grn_numbers ?? []
  const focus = data.review_focus || (data.review_category ? reviewCategoryLabel(data.review_category) : null)

  return (
    <div className="rounded-xl border border-border/50 bg-white p-3 shadow-soft">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-brand-navy">{data.invoice_id || attachment.title}</p>
          <p className="truncate text-xs text-muted-foreground">{data.vendor_name || attachment.description || '—'}</p>
        </div>
        <StatusPill status={data.status} />
      </div>

      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs sm:grid-cols-3">
        <div>
          <dt className="text-muted-foreground">Total</dt>
          <dd className="font-medium text-foreground">{formatMoney(data.grand_total, data.currency)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Invoice date</dt>
          <dd className="text-foreground">{formatCalendarDate(data.invoice_date)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Due</dt>
          <dd className="text-foreground">{formatCalendarDate(data.due_date)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">PO</dt>
          <dd className="truncate text-foreground">{pos.length ? pos.join(', ') : '—'}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">GRN</dt>
          <dd className="truncate text-foreground">{grns.length ? grns.join(', ') : '—'}</dd>
        </div>
        {data.match_confidence != null && (
          <div>
            <dt className="text-muted-foreground">Confidence</dt>
            <dd className="text-foreground">{Math.round(data.match_confidence * (data.match_confidence <= 1 ? 100 : 1))}%</dd>
          </div>
        )}
      </dl>

      {focus && (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-700">
          <Icons.alertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.5} />
          <span>Review focus: {focus}</span>
        </p>
      )}

      {id != null && (
        <Link
          href={workbenchLink(id, data.queue, data.status)}
          onClick={onNavigate}
          className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-brand-purple hover:underline"
        >
          Open in Workbench <Icons.arrowRight className="h-3 w-3" strokeWidth={1.5} />
        </Link>
      )}
    </div>
  )
}
